import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Clock, Info, ShieldQuestion } from "lucide-react";
import { cn } from "../../lib/cn";
import { CitationRow } from "../CitationRow";

/**
 * ChatCitations + SourcesPanel - the cited-sources surface for the
 * collective-intelligence chat. One source of truth both Creator Studio
 * (FE-React) and the Studio template (AIStudio-v3) consume to render the
 * `data-citation` parts the runtime streams (see ais-agent-runtime
 * `docs/contracts/stream-protocol.md` + `block-registry.md`).
 *
 * What it renders:
 *   - inline numbered `[n]` markers (via `CitationRow`'s anchor variant) wired
 *     to a collapsible sources panel; hover / focus previews the source;
 *   - sources deduped by URL and numbered in first-seen order, so a source
 *     cited twice gets one number and one panel row;
 *   - per-claim confidence shading (optional `support` in [0, 1]);
 *   - a relative-time freshness badge per source (optional `freshness`);
 *   - an explicit "I can't ground this" abstention state (optional
 *     `abstained`).
 *
 * Dormant-until-runtime (F6 in the contract): `support`, `freshness`, and
 * `abstained` are OPTIONAL. The citation gate that emits them is not built yet;
 * when a field is absent, its affordance renders nothing and the numbered
 * markers + panel stand on their own. When the gate lights them up, the same
 * components react with no further change.
 *
 * Composition: the inline `[n]` marker is `CitationRow` (the existing anchor +
 * popover primitive), so the marker chrome, a11y, and motion are not
 * re-implemented here. This component owns the view-model (dedup + numbering),
 * the panel, and the F6 affordances.
 *
 * Theming is entirely token-driven (`bg-card`, `text-muted-foreground`,
 * `text-success`, `border-border`, ...) - no hardcoded colors. SSR-safe: the
 * freshness clock is read via `useSyncExternalStore` with a stable `null`
 * server snapshot, so there is no hydration mismatch.
 */

/* ============================================================================
 * Model - the streamed citation shape + the deduped view-model
 * ========================================================================= */

/** A cited source. `freshness` is optional (dormant until the gate emits it). */
export interface CitationSource {
  /** Source display name. */
  title: string;
  /** Source URL. http(s) is treated as navigable; anything else renders inert. */
  url: string;
  /** Short supporting excerpt. */
  snippet?: string;
  /**
   * When the source was published / updated. ISO string or epoch millis;
   * rendered as a relative-time badge. Absent -> no badge.
   */
  freshness?: string | number;
}

/**
 * One streamed `data-citation` part. `claim_ref` groups citations that back the
 * same claim so the UI can shade confidence + abstain per claim, not per turn.
 */
export interface CitationData {
  /** Groups citations that support the same claim. */
  claim_ref?: string;
  /** The cited source. */
  source: CitationSource;
  /** Per-claim support score in [0, 1]. Drives shading; absent -> dormant. */
  support?: number;
  /** Explicit "couldn't ground this claim" signal. Absent / false -> dormant. */
  abstained?: boolean;
}

/** A deduped, numbered source plus the best support seen across its citations. */
export interface CitationSourceEntry {
  /** Stable 1-based number shown in the marker and the panel. */
  n: number;
  /** The merged source metadata (richest seen across duplicate citations). */
  source: CitationSource;
  /** Best (max) support across the citations that pointed at this source. */
  support?: number;
  /** True if any citation for this source was an explicit abstention. */
  abstained: boolean;
}

/** Markers for one claim (or the single synthesis group when no `claim_ref`). */
export interface CitationClaim {
  /** `claim_ref`, or null for the ungrouped synthesis. */
  ref: string | null;
  /** 1-based source numbers this claim cites, in first-seen order. */
  numbers: number[];
  /** Best (max) support across this claim's citations; undefined if none had it. */
  support?: number;
  /** True if the claim was explicitly abstained. */
  abstained: boolean;
}

/** The whole citation view-model for a turn. */
export interface CitationModel {
  /** Deduped sources in first-seen order, numbered from 1. */
  sources: CitationSourceEntry[];
  /** Claims grouped by `claim_ref`; a single null-ref group when ungrouped. */
  claims: CitationClaim[];
}

function sourceKey(url: string): string {
  return url.trim().toLowerCase();
}

function mergeSource(a: CitationSource, b: CitationSource): CitationSource {
  return {
    title: a.title || b.title,
    url: a.url || b.url,
    snippet: a.snippet ?? b.snippet,
    freshness: a.freshness ?? b.freshness,
  };
}

function maxDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (typeof a === "number" && typeof b === "number") return Math.max(a, b);
  return typeof a === "number" ? a : typeof b === "number" ? b : undefined;
}

/**
 * Build the view-model from the flat streamed citation list.
 *
 * Dedup is by URL (trimmed, case-insensitive): the same source cited for two
 * claims gets one number and one panel row. Numbers are assigned in first-seen
 * order so they stay stable as the stream appends. Claims are grouped by
 * `claim_ref`; citations without one fold into a single synthesis group.
 * Citations whose `source.url` is not a string are skipped (defensive against a
 * malformed stream).
 */
export function buildCitationModel(citations: CitationData[]): CitationModel {
  const byKey = new Map<string, CitationSourceEntry>();
  const claimOrder: string[] = [];
  const claimMap = new Map<string, CitationClaim>();

  for (const c of citations ?? []) {
    if (!c?.source || typeof c.source.url !== "string") continue;
    const key = sourceKey(c.source.url);

    let entry = byKey.get(key);
    if (!entry) {
      entry = { n: byKey.size + 1, source: c.source, support: undefined, abstained: false };
      byKey.set(key, entry);
    } else {
      entry.source = mergeSource(entry.source, c.source);
    }
    entry.support = maxDefined(entry.support, c.support);
    if (c.abstained) entry.abstained = true;

    const ref = c.claim_ref ?? "";
    let claim = claimMap.get(ref);
    if (!claim) {
      claim = { ref: c.claim_ref ?? null, numbers: [], support: undefined, abstained: false };
      claimMap.set(ref, claim);
      claimOrder.push(ref);
    }
    if (!claim.numbers.includes(entry.n)) claim.numbers.push(entry.n);
    claim.support = maxDefined(claim.support, c.support);
    if (c.abstained) claim.abstained = true;
  }

  return {
    sources: [...byKey.values()],
    claims: claimOrder.map((ref) => claimMap.get(ref)!),
  };
}

/* ============================================================================
 * URL helpers (shared)
 * ========================================================================= */

function safeUrl(url?: string): string | undefined {
  return url && /^https?:\/\//i.test(url) ? url : undefined;
}

function hostOf(url?: string): string | undefined {
  const safe = safeUrl(url);
  if (!safe) return undefined;
  try {
    return new URL(safe).host.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

/* ============================================================================
 * Confidence (F6 - dormant when support is absent)
 * ========================================================================= */

export type ConfidenceTier = "high" | "medium" | "low";

/** Map a 0..1 support score to a tier. Tuned to de-emphasize low support. */
export function confidenceTier(support: number): ConfidenceTier {
  if (support >= 0.75) return "high";
  if (support >= 0.45) return "medium";
  return "low";
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

export interface ConfidenceShadeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Support score in [0, 1]. Absent / NaN -> children render unchanged. */
  support?: number;
  children: React.ReactNode;
}

/**
 * Shade children by per-claim confidence. DORMANT when `support` is absent: the
 * children render unchanged with no wrapper. With a score, low-support content
 * is de-emphasized (reduced opacity + a left rule) so the eye discounts
 * weakly-grounded claims.
 */
export const ConfidenceShade = React.forwardRef<HTMLDivElement, ConfidenceShadeProps>(
  function ConfidenceShade({ support, children, className, ...rest }, ref) {
    if (typeof support !== "number" || Number.isNaN(support)) {
      return <>{children}</>;
    }
    const tier = confidenceTier(support);
    const pct = Math.round(clamp01(support) * 100);
    return (
      <div
        ref={ref}
        data-confidence={tier}
        className={cn(
          "border-l-2 pl-2 transition-opacity motion-reduce:transition-none",
          tier === "high" && "border-border opacity-100",
          tier === "medium" && "border-accent/50 opacity-90",
          tier === "low" && "border-muted-foreground/40 opacity-60",
          className,
        )}
        title={`Support ${pct}%`}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ConfidenceShade.displayName = "ConfidenceShade";

const confidenceChipVariants = cva(
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
  {
    variants: {
      tier: {
        high: "bg-success/15 text-success",
        medium: "bg-muted text-muted-foreground",
        low: "bg-muted text-muted-foreground line-through decoration-1",
      },
    },
    defaultVariants: { tier: "medium" },
  },
);

/** A compact confidence chip (used in the panel rows + preview). Dormant when absent. */
function ConfidenceChip({ support }: { support?: number }) {
  if (typeof support !== "number" || Number.isNaN(support)) return null;
  const tier = confidenceTier(support);
  const pct = Math.round(clamp01(support) * 100);
  const label = tier === "high" ? "well grounded" : tier === "medium" ? "partial" : "weak";
  return (
    <span className={confidenceChipVariants({ tier })} title={`Support ${pct}%`}>
      {label} {pct}%
    </span>
  );
}

/* ============================================================================
 * Freshness (F6 - dormant when freshness is absent)
 * ========================================================================= */

/**
 * The current time bucketed to the minute, or null on the server / before
 * hydration. `useSyncExternalStore` keeps the snapshot stable within a minute
 * (no setState-in-effect); a 60s tick re-reads it so relative times age.
 */
function useClientNow(): number | null {
  return React.useSyncExternalStore(subscribeMinute, getMinuteSnapshot, () => null);
}

function subscribeMinute(onChange: () => void): () => void {
  const id = setInterval(onChange, 60_000);
  return () => clearInterval(id);
}

function getMinuteSnapshot(): number {
  return Math.floor(Date.now() / 60_000) * 60_000;
}

function parseFreshness(value?: string | number): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  const t = Date.parse(trimmed);
  return Number.isNaN(t) ? null : t;
}

function relativeTime(then: number, now: number): string {
  const diff = now - then;
  const future = diff < 0;
  const s = Math.abs(diff) / 1000;
  const units: Array<[number, string]> = [
    [60, "s"],
    [60, "m"],
    [24, "h"],
    [7, "d"],
    [4.345, "w"],
    [12, "mo"],
    [Number.POSITIVE_INFINITY, "y"],
  ];
  let value = s;
  let label = "s";
  for (let i = 0; i < units.length; i++) {
    const [div, name] = units[i];
    if (value < div || i === units.length - 1) {
      label = name;
      break;
    }
    value = value / div;
  }
  const rounded = Math.max(0, Math.floor(value));
  if (label === "s" && rounded < 30) return future ? "soon" : "just now";
  return future ? `in ${rounded}${label}` : `${rounded}${label} ago`;
}

export interface FreshnessBadgeProps {
  /** ISO timestamp or epoch millis. Absent / unparseable -> renders nothing. */
  value?: string | number;
  /** Extra classes appended to the badge. */
  className?: string;
}

/**
 * Relative-time freshness badge. DORMANT when `value` is absent or unparseable
 * (renders nothing). SSR-safe: server + first client render show the neutral
 * "updated" label (no hydration mismatch); after hydration the client clock
 * resolves the relative time and refreshes it each minute.
 */
export function FreshnessBadge({ value, className }: FreshnessBadgeProps) {
  const at = React.useMemo(() => parseFreshness(value), [value]);
  const now = useClientNow();

  if (at == null) return null;
  const text = now == null ? "updated" : relativeTime(at, now);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground",
        className,
      )}
      title={new Date(at).toISOString()}
    >
      <Clock className="size-2.5" aria-hidden />
      {text}
    </span>
  );
}

/* ============================================================================
 * Source preview (hover / focus) - the body of the inline marker's popover
 * ========================================================================= */

function SourcePreview({ entry }: { entry: CitationSourceEntry }) {
  const { source } = entry;
  const safe = safeUrl(source.url);
  const host = hostOf(source.url);
  return (
    <div className="flex flex-col gap-1.5 text-left">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md border border-border bg-card px-1 text-[10px] font-semibold tabular-nums text-muted-foreground">
          {entry.n}
        </span>
        {host ? <span className="truncate text-[11px] text-muted-foreground">{host}</span> : null}
        <span className="ml-auto flex items-center gap-1">
          <ConfidenceChip support={entry.support} />
          <FreshnessBadge value={source.freshness} />
        </span>
      </div>
      <div className="text-sm font-medium text-foreground">{source.title || host || "Source"}</div>
      {source.snippet ? (
        <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
          {source.snippet}
        </p>
      ) : null}
      {safe ? (
        <span className="truncate text-xs text-primary underline underline-offset-2">{safe}</span>
      ) : null}
    </div>
  );
}

/* ============================================================================
 * Inline markers
 * ========================================================================= */

export interface CitationMarkerProps {
  /** The deduped, numbered source this marker points at. */
  entry: CitationSourceEntry;
  /** Fired with the source number on activation (e.g. open the panel). */
  onActivate?: (n: number) => void;
  /** Extra classes forwarded to the anchor chip. */
  className?: string;
}

/**
 * One inline `[n]` marker. Renders `CitationRow`'s anchor variant - the
 * existing primitive owns the chip chrome, the hover / focus popover, the
 * accessible name, and the motion. We feed it the source preview as the popover
 * body and bridge `onActivate` to the source number.
 */
export const CitationMarker = React.forwardRef<HTMLElement, CitationMarkerProps>(
  function CitationMarker({ entry, onActivate, className }, ref) {
    const accessibleName = `Source ${entry.n}: ${
      entry.source.title || hostOf(entry.source.url) || "source"
    }`;
    return (
      <CitationRow
        ref={ref}
        variant="anchor"
        index={entry.n}
        title={entry.source.title || hostOf(entry.source.url) || "source"}
        summary={entry.source.snippet}
        href={safeUrl(entry.source.url)}
        onActivate={() => onActivate?.(entry.n)}
        aria-label={accessibleName}
        className={className}
        data-citation-marker={entry.n}
        renderPreview={<SourcePreview entry={entry} />}
      />
    );
  },
);

CitationMarker.displayName = "CitationMarker";

export interface CitationMarkersProps {
  /** The sources to render markers for, e.g. `[1][2]`. */
  entries: CitationSourceEntry[];
  /** Fired with a source number when a marker is activated. */
  onActivate?: (n: number) => void;
  /** Extra classes for the wrapping strip. */
  className?: string;
}

/**
 * The inline marker strip for a claim's sources, e.g. `[1][2]`. The tail-marker
 * pattern (Perplexity / Glean): markers sit at the end of the text they ground,
 * since the model text is rendered by a sealed `Markdown` component and is not
 * mutated. Renders nothing for an empty list.
 */
export function CitationMarkers({ entries, onActivate, className }: CitationMarkersProps) {
  if (!entries || entries.length === 0) return null;
  return (
    <span className={cn("inline-flex items-center gap-0.5 whitespace-nowrap", className)}>
      {entries.map((e) => (
        <CitationMarker key={e.n} entry={e} onActivate={onActivate} />
      ))}
    </span>
  );
}

/* ============================================================================
 * Abstention (F6 - dormant unless signaled)
 * ========================================================================= */

export interface AbstentionNoticeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, render the notice. Absent / false -> renders nothing. */
  abstained?: boolean;
  /** Whether the notice is for the whole turn (`this`) or one `claim`. */
  scope?: "this" | "claim";
}

/**
 * The explicit "I can't ground this" state. Renders only when `abstained` is
 * true (dormant otherwise). Use at the turn level (`run_end.abstained`) or per
 * claim (`citation.abstained`).
 */
export const AbstentionNotice = React.forwardRef<HTMLDivElement, AbstentionNoticeProps>(
  function AbstentionNotice({ abstained, scope = "this", className, ...rest }, ref) {
    if (!abstained) return null;
    return (
      <div
        ref={ref}
        role="status"
        className={cn(
          "flex items-start gap-2 rounded-xl border border-dashed border-border bg-muted/40 px-3 py-2",
          className,
        )}
        data-slot="abstention-notice"
        {...rest}
      >
        <ShieldQuestion className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <p className="text-xs leading-relaxed text-muted-foreground">
          I couldn't ground {scope === "claim" ? "this claim" : "this"} in the sources I found.
          Rather than guess, I'm flagging it as unverified.
        </p>
      </div>
    );
  },
);

AbstentionNotice.displayName = "AbstentionNotice";

/* ============================================================================
 * Sources panel
 * ========================================================================= */

export interface SourcesPanelProps extends Omit<
  React.HTMLAttributes<HTMLDetailsElement>,
  "onToggle"
> {
  /** The deduped view-model. */
  model: CitationModel;
  /** Bump to request the panel open (e.g. a marker was clicked). */
  openSignal?: number;
  /** Source number to scroll to / highlight when opened. */
  highlight?: number | null;
  /** Start expanded. Default `false`. */
  defaultOpen?: boolean;
}

/**
 * A collapsible panel listing every deduped cited source, numbered to match the
 * inline markers. Header reads "Synthesized across N sources". Built on
 * `<details>` so it is keyboard-operable and works without JS; opening can be
 * driven externally (a marker click bumps `openSignal` + sets `highlight`) or
 * by the user toggling the summary. Renders nothing when there are no sources.
 */
export const SourcesPanel = React.forwardRef<HTMLDetailsElement, SourcesPanelProps>(
  function SourcesPanel(
    { model, openSignal, highlight, defaultOpen = false, className, ...rest },
    forwardedRef,
  ) {
    const { sources } = model;
    const [open, setOpen] = React.useState(defaultOpen);
    const rowRefs = React.useRef(new Map<number, HTMLLIElement | null>());

    // Adjust open state when a new openSignal arrives (a marker was clicked).
    // Storing the last-seen signal in state + comparing during render lets React
    // reconcile in one pass instead of an effect that triggers a second render.
    // See https://react.dev/learn/you-might-not-need-an-effect.
    const [seenSignal, setSeenSignal] = React.useState(openSignal);
    if (openSignal !== seenSignal) {
      setSeenSignal(openSignal);
      if (openSignal != null) setOpen(true);
    }

    // Scrolling to the highlighted row is a genuine DOM-after-layout effect.
    // `scrollIntoView` is feature-checked: jsdom and some embedded webviews
    // don't implement it, and a missing method must not throw in an effect.
    React.useEffect(() => {
      if (!open || highlight == null) return;
      const el = rowRefs.current.get(highlight);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    }, [open, highlight, openSignal]);

    if (sources.length === 0) return null;

    return (
      <details
        ref={forwardedRef}
        open={open}
        onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
        className={cn("border-t border-border pt-2", className)}
        data-slot="sources-panel"
        {...rest}
      >
        <summary
          className={cn(
            "flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background rounded",
            "hover:text-foreground transition-colors",
          )}
        >
          <Info className="size-3" aria-hidden />
          Synthesized across {sources.length} {sources.length === 1 ? "source" : "sources"}
        </summary>
        <ol className="mt-2 flex flex-col gap-1.5">
          {sources.map((entry) => (
            <SourceRow
              key={entry.n}
              entry={entry}
              highlighted={highlight === entry.n}
              ref={(el) => {
                rowRefs.current.set(entry.n, el);
              }}
            />
          ))}
        </ol>
      </details>
    );
  },
);

SourcesPanel.displayName = "SourcesPanel";

const SourceRow = React.forwardRef<
  HTMLLIElement,
  { entry: CitationSourceEntry; highlighted: boolean }
>(function SourceRow({ entry, highlighted }, ref) {
  const { source } = entry;
  const safe = safeUrl(source.url);
  const host = hostOf(source.url);
  return (
    <li
      ref={ref}
      className={cn(
        "flex gap-2 rounded-md px-1 py-1 transition-colors motion-reduce:transition-none",
        highlighted && "bg-accent/10",
      )}
    >
      <span className="mt-0.5 flex h-[1.15rem] min-w-[1.15rem] items-center justify-center rounded bg-muted px-1 text-[11px] font-medium leading-none tabular-nums text-muted-foreground">
        {entry.n}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          {safe ? (
            <a
              href={safe}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline underline-offset-2 hover:no-underline"
            >
              {source.title || host || safe}
            </a>
          ) : (
            <span className="text-sm text-foreground">{source.title || "source"}</span>
          )}
          <ConfidenceChip support={entry.support} />
          <FreshnessBadge value={source.freshness} />
        </span>
        {source.snippet ? (
          <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
            {source.snippet}
          </span>
        ) : null}
      </span>
    </li>
  );
});

/* ============================================================================
 * ChatCitations - the orchestrator
 * ========================================================================= */

const chatCitationsVariants = cva("flex flex-col gap-2 text-sm", {
  variants: {
    spacing: {
      default: "mt-3",
      tight: "mt-1.5",
      none: "mt-0",
    },
  },
  defaultVariants: { spacing: "default" },
});

export interface ChatCitationsProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof chatCitationsVariants> {
  /**
   * The streamed citations for this turn (the `data-citation` parts). Deduped +
   * numbered internally, so pass them in arrival order. Pass either this OR a
   * prebuilt `model` (if both are passed, `model` wins).
   */
  citations?: CitationData[];
  /**
   * A prebuilt view-model (from `buildCitationModel`). Pass this when the
   * consumer already holds the model (e.g. it drives inline markers elsewhere)
   * to avoid rebuilding it. Overrides `citations`.
   */
  model?: CitationModel;
  /**
   * Turn-level abstention (`run_end.abstained`). When true, the abstention
   * notice renders above the panel. Per-claim abstention is read from the model.
   */
  abstained?: boolean;
  /** Start the sources panel expanded. Default `false`. */
  defaultOpen?: boolean;
  /** Render the trailing inline marker strip (`[1][2]...`) above the panel. */
  showMarkers?: boolean;
}

/**
 * Top-level cited-sources surface for an assistant turn. Renders (in order):
 *   1. the turn-level abstention notice (when `abstained`),
 *   2. an optional trailing inline marker strip (`showMarkers`),
 *   3. the collapsible sources panel.
 *
 * Clicking an inline marker opens the panel and highlights that source. For
 * markers placed inside the message body (the common case), render
 * `CitationMarkers` there and drive a shared `SourcesPanel` directly - this
 * orchestrator is the batteries-included default for the simplest layout.
 *
 * Renders nothing when there are no sources and no abstention to show.
 */
export const ChatCitations = React.forwardRef<HTMLDivElement, ChatCitationsProps>(
  function ChatCitations(
    {
      citations,
      model: modelProp,
      abstained,
      defaultOpen,
      showMarkers = false,
      spacing,
      className,
      ...rest
    },
    ref,
  ) {
    const model = React.useMemo(
      () => modelProp ?? buildCitationModel(citations ?? []),
      [modelProp, citations],
    );

    const [openSignal, setOpenSignal] = React.useState(0);
    const [highlight, setHighlight] = React.useState<number | null>(null);

    const activate = React.useCallback((n: number) => {
      setHighlight(n);
      setOpenSignal((s) => s + 1);
    }, []);

    const hasSources = model.sources.length > 0;
    if (!hasSources && !abstained) return null;

    return (
      <div
        ref={ref}
        className={cn(chatCitationsVariants({ spacing }), className)}
        data-slot="chat-citations"
        {...rest}
      >
        <AbstentionNotice abstained={abstained} scope="this" />
        {showMarkers && hasSources ? (
          <CitationMarkers entries={model.sources} onActivate={activate} />
        ) : null}
        <SourcesPanel
          model={model}
          openSignal={openSignal}
          highlight={highlight}
          defaultOpen={defaultOpen}
        />
      </div>
    );
  },
);

ChatCitations.displayName = "ChatCitations";

export {
  chatCitationsVariants,
  confidenceChipVariants,
  safeUrl as citationSafeUrl,
  hostOf as citationHostOf,
};
export type ChatCitationsVariantProps = VariantProps<typeof chatCitationsVariants>;
